import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const BUSINESS_TYPES = ["Small Business", "Growing Company", "NGO", "Training Organization", "Skill Development Organization", "Educational Institute", "Multi-Center Organization", "Other"];

export function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useSEO({ title: "Login — Mashara Finance", description: "Sign in to your Mashara Finance account.", path: "/login", noindex: true });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      navigate(user.role === "admin" ? "/admin" : "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="login-page" className="py-16 sm:py-24">
      <div className="max-w-md mx-auto px-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <h1 className="font-display text-2xl font-bold text-slate-900">Welcome back</h1>
          <p className="text-sm text-slate-600 mt-1">Sign in to your Mashara Finance account.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="login-email">Email</Label>
              <Input id="login-email" data-testid="login-email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="login-password">Password</Label>
              <Input id="login-password" data-testid="login-password-input" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="login-submit-button" disabled={loading}
              className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white py-6 font-semibold">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
          <p className="text-sm text-slate-600 text-center mt-6">
            New to Mashara Finance? <Link to="/signup" data-testid="login-signup-link" className="text-emerald-700 font-semibold">Start your free trial</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function SignupPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", org_name: "", mobile: "", email: "", password: "", business_type: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useSEO({
    title: "Start Your Free Trial — Mashara Finance",
    description: "Create your Mashara Finance account. 10-day free trial of the full platform, activated with a ₹1 verification payment.",
    path: "/signup",
  });

  const plan = params.get("plan");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register({ ...form, business_type: form.business_type || null });
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="signup-page" className="py-16 sm:py-24">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
        <div className="hidden lg:block">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-700 mb-3">Free Trial</p>
          <h1 className="font-display text-4xl font-bold tracking-tight text-slate-900 leading-tight">
            Start Your 10-Day Free Trial
          </h1>
          <p className="mt-4 text-slate-600 leading-relaxed">
            Create your account, verify with a ₹1 payment, and get full access to Finance, HR, Attendance, Payroll, Expenses, Invoices and Reports.
          </p>
          <div className="mt-8 space-y-3">
            {["Full platform access during trial", "Guided onboarding checklist", "Your data stays exportable", "Cancel anytime — no lock-in", plan ? `Selected plan: ${plan}` : "Choose your plan after the trial"].filter(Boolean).map((p) => (
              <div key={p} className="flex items-center gap-2.5 text-slate-700">
                <Check className="w-5 h-5 text-emerald-600 shrink-0" /> {p}
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
          <h2 className="font-display text-2xl font-bold text-slate-900 lg:hidden">Start Your 10-Day Free Trial</h2>
          <h2 className="font-display text-2xl font-bold text-slate-900 hidden lg:block">Create your account</h2>
          <p className="text-sm text-slate-600 mt-1">Takes less than a minute. Trial activated with a ₹1 verification payment.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="signup-form">
            <div className="space-y-1.5">
              <Label htmlFor="signup-name">Your Name *</Label>
              <Input id="signup-name" data-testid="signup-name-input" required minLength={2} value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-org">Organization Name *</Label>
              <Input id="signup-org" data-testid="signup-org-input" required minLength={2} value={form.org_name}
                onChange={(e) => setForm({ ...form, org_name: e.target.value })} placeholder="Your organization" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-mobile">Mobile *</Label>
              <Input id="signup-mobile" data-testid="signup-mobile-input" required minLength={8} value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="98765 43210" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-email">Email *</Label>
              <Input id="signup-email" data-testid="signup-email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="signup-password">Password *</Label>
              <Input id="signup-password" data-testid="signup-password-input" type="password" required minLength={8} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Minimum 8 characters" />
            </div>
            <div className="space-y-1.5">
              <Label>Business Type</Label>
              <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
                <SelectTrigger data-testid="signup-business-type-select"><SelectValue placeholder="Select your organization type" /></SelectTrigger>
                <SelectContent>
                  {BUSINESS_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {error && <p data-testid="signup-error" className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>}
            <Button type="submit" data-testid="signup-submit-button" disabled={loading}
              className="w-full rounded-xl bg-emerald-700 hover:bg-emerald-800 text-white py-6 font-semibold">
              {loading ? "Creating your account…" : "Create Account & Start Trial"}
            </Button>
          </form>
          <p className="text-sm text-slate-600 text-center mt-6">
            Already have an account? <Link to="/login" data-testid="signup-login-link" className="text-emerald-700 font-semibold">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
